import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Loader2, X, ZoomIn } from 'lucide-react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../utils/getCroppedImg';
import './AddProductForm.css';
import { useNavigate } from 'react-router-dom';

// const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
//const API_URL ='http://localhost:5000/api';
// const initialSizes = Object.fromEntries(SIZES.map(s => [s, false]));


const AddProductForm = ({
  onProductAdded,
  mode = 'add',
  productId = null
}) => {
    // Basic Form State
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('Design Wear');
    const [price, setPrice] = useState('');
     const [salePrice, setSalePrice] = useState('');
    const [description, setDescription] = useState('');
    // const [sizes, setSizes] = useState(initialSizes);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');

    // Cropper State
    const [croppingImage, setCroppingImage] = useState(null); // The original file for cropping
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropper, setShowCropper] = useState(false);

    const fileInputRef = useRef(null);
const navigate = useNavigate();


useEffect(() => {
  if (mode === 'edit' && productId) {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${API_URL}/products/${productId}`);
        const data = await res.json();

        if (data.success) {
          const product = data.data;

          setTitle(product.name);
          setCategory(product.category);
          setPrice(product.price);
          setSalePrice(product.sale_price);
          setDescription(product.description);
          setImagePreview(product.image_url);
        }
      } catch (err) {
        console.error('Failed to fetch product', err);
      }
    };

    fetchProduct();
  }
}, [mode, productId]);


    const onCropComplete = useCallback((_extendedArea, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setCroppingImage(reader.result);
                setShowCropper(true);
            });
            reader.readAsDataURL(file);
        }
    };

    const handleSaveCrop = async () => {
        try {
            const croppedImageBlob = await getCroppedImg(croppingImage, croppedAreaPixels);
            
            // Create a preview for the UI
            const previewUrl = URL.createObjectURL(croppedImageBlob);
            setImagePreview(previewUrl);
            
            // Set the image file for submission
            setImage(croppedImageBlob);
            
            // Close cropper
            setShowCropper(false);
            setCroppingImage(null);
        } catch (e) {
            console.error('Error cropping image:', e);
        }
    };

    const handleCancelCrop = () => {
        setShowCropper(false);
        setCroppingImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const resetForm = () => {
        setImage(null);
        setImagePreview(null);
        setTitle('');
        setCategory('Design Wear');
        setPrice('');
        setSalePrice('');
        setDescription('');
        setFormError('');
        // setSizes(initialSizes);
    };

   const handleSubmit = async (e) => {
     e.preventDefault();
     setLoading(true);
     setFormError('');

     const formData = new FormData();
     formData.append('name', title);
     formData.append('description', description);
     formData.append('price', price);
     formData.append('sale_price', salePrice);
     formData.append('stock_quantity', 0);
     formData.append('category', category);

     // ✅ Only require image for ADD
     if (image) {
       formData.append('image', image, 'product.jpg');
     } else if (mode === 'add') {
       setFormError('Product image is required');
       setLoading(false);
       return;
     }

     try {
       const url =
         mode === 'edit'
           ? `${API_URL}/products/${productId}`
           : `${API_URL}/products`;

       const method = mode === 'edit' ? 'PUT' : 'POST';

       const res = await fetch(url, {
         method,
         body: formData,
       });

       const data = await res.json();

       if (data.success) {
         if (mode === 'add') {
           resetForm();
           onProductAdded && onProductAdded();
         }

         if (mode === 'edit') {
           navigate('/admin'); // 👈 go back to dashboard
         }
       } else {
         setFormError(data.message || 'Something went wrong');
       }
     } catch (err) {
       console.error(err);
       setFormError('Failed to save product');
     } finally {
       setLoading(false);
     }
   };

    return (
        <div className="add-product-form-container slide-down">
            {/* Image Cropper Modal */}
            {showCropper && (
                <div className="cropper-modal-overlay">
                    <div className="cropper-container">
                        <Cropper
                            image={croppingImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={4 / 5} // 1000x1250 target ratio
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </div>
                    <div className="cropper-controls">
                        <div className="zoom-control">
                            <label><ZoomIn size={16} /> Zoom</label>
                            <input
                                type="range"
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                aria-labelledby="Zoom"
                                onChange={(e) => setZoom(e.target.value)}
                                className="zoom-slider"
                            />
                        </div>
                        <div className="cropper-buttons">
                            <button type="button" className="btn-cancel-crop" onClick={handleCancelCrop}>
                                Cancel
                            </button>
                            <button type="button" className="btn-save-crop" onClick={handleSaveCrop}>
                                Save Crop
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="form-header">

<h2>{mode === 'edit' ? 'Edit Product' : 'Upload New Product'}</h2>
<p>
  {mode === 'edit'
    ? 'Update product details'
    : 'Fill in the details to add a new product'}
</p>

            </div>

            <form onSubmit={handleSubmit} className="product-form">
               {mode === 'add' &&( <div className="form-group image-upload-group">
                    <label>Product Image * (1000x1250px recommended)</label>
                    <div className="image-dropzone" onClick={() => fileInputRef.current.click()}>
                        <input
                            type="file"
                            accept="image/jpeg, image/png"
                            className="hidden-file-input"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                        />
                        {imagePreview ? (
                            <div className="preview-container">
                                <img src={imagePreview} alt="Preview" className="image-preview" />
                                <button
                                    type="button"
                                    className="remove-image-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setImage(null);
                                        setImagePreview(null);
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <div className="upload-placeholder">
                                <Upload size={32} className="upload-icon" />
                                <p className="upload-main-text">Click to upload image</p>
                                <p className="upload-sub-text">Will be cropped to 4:5 ratio</p>
                            </div>
                        )}
                    </div>
                </div>)}

                <div className="form-row">
                    <div className="form-group">
                        <label>Product Title *</label>
                        <input
                            type="text"
                            placeholder="e.g., Elegant Silk Saree"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Category *</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} required>
                            <option value="Design Wear">Design Wear</option>
                            <option value="Dresses">Dresses</option>
                            <option value="Blouses">Blouses</option>
                        </select>
                    </div>
                </div>
<div className="form-row">
                <div className="form-group half-width">
                    <label>Original Price (₹) *</label>
                    <input
                        type="number"
                        placeholder="e.g., 15000"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        required
                        min="0"
                    />
                </div>
               <div className="form-group half-width">
                    <label>Sale Price (₹) *</label>
                    <input
                        type="number"
                        placeholder="e.g., 15000"
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        required
                        min="0"
                    />
                </div>
                </div>
                <div className="form-group">
                    <label>Description *</label>
                    <textarea
                        placeholder="Enter product description..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                        rows="4"
                    />
                </div>

                {/* 
                <div className="form-group sizes-group">
                    <label>Sizes *</label>
                    <div className="sizes-container">
                        {SIZES.map((size) => (
                            <label
                                key={size}
                                className={`size-checkbox-label ${sizes[size] ? 'selected' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={sizes[size]}
                                    onChange={() => setSizes(prev => ({ ...prev, [size]: !prev[size] }))}
                                    className="hidden-checkbox"
                                />
                                <span className="size-text">{size}</span>
                            </label>
                        ))}
                    </div>
                </div>
                */}

                {formError && (
                    <div className="form-error-message fade-in">
                        {formError}
                    </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-product-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="btn-loading">
                        <Loader2 size={18} className="spin-icon" />
                        {mode === 'edit' ? 'Updating...' : 'Adding...'}
                      </span>
                    ) : (
                      mode === 'edit' ? 'Update Product' : '+ Add Product'
                    )}
                  </button>

                  {mode === 'edit' && (
                    <button
                      type="button"
                      className="submit-product-btn"
                      onClick={() => navigate('/admin')}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  )}
                </div>

            </form>
        </div>
    );
};

export default AddProductForm;
